const $ = window.$, _ = window._

import React from 'react'
import {trackEventSP} from 'js/studios/shared/tracking'
import * as Sentry from "@sentry/react"

/******** General Auth *****/

/**
 * Sends ajax request to our RegModal API
 * @return {jQuery.promise}
 */
export function sendRequest(method, data) {
  data = Object.assign(data, {'_method': method})
  let dfd = $.Deferred()
  $.ajax({
    type: "POST",
    url: window.cm.urls['reg-modal-api'],
    data: JSON.stringify(data),
    success: function(response){
      if (['login', 'signupFull', 'facebookLogin', 'googleLogin', 'appleLogin'].indexOf(method) > -1){
        window.cm.store.dispatch({
          type: 'USER_LOGIN',
          user: response
        })
        $('[name="csrf-token"]').attr('content', response.csrf_token); // update csrf token
      }
      dfd.resolve(response)
    }.bind(this),
    error: function(response){

      // figure out error message and only take first one.. as that's the only one i can display
      let errorMessage = ''
      if (response.responseJSON && !_.isEmpty(response.responseJSON)){
        let errors = response.responseJSON.errors
        if (typeof errors === 'string'){
          errorMessage = errors
        } else{
          errorMessage = window._.values(response.responseJSON.errors)[0][0]
        }
      } else if (response.status === 400) {
        errorMessage = response.responseText
      } else {
        errorMessage = "Something went wrong. If this problem persists, please contact support"
      }

      dfd.reject(errorMessage)
    }.bind(this)
  })

  return dfd.promise()
}




/******** Facebook *********/

/**
 * Inner function to check facebook status. Needed as we need to recursively attempt, potentially waiting for facebook to load.
 */
function _checkFacebookStatus(attempt, dfd){
  if (!window.FB){
    trackEventSP('wait_facebook_not_loaded', 'Customer sign up', {
      attempt: attempt
    })
    if (attempt > 3){
      trackEventSP('could_not_load_facebook_successfully', 'Customer sign up', {
        attempt: attempt
      })
      dfd.reject("Could not reach facebook. Please refresh the page and try again.")
      return null;  // do not need to proceed any furnther
    } else {
      window.setTimeout(() => _checkFacebookStatus(attempt + 1, dfd), 1000)  // recursive call to try again (why we need the inner function)
      return null;  // do not need to proceed any furnther
    }
  } else if (attempt > 1){
    trackEventSP('facebook_successfully_loaded', 'Customer sign up', {
      attempt: attempt
    })
  }

  window.FB.getLoginStatus(function(response) {
    if (response.status === "connected") {
      dfd.resolve(response)
    }
    else {
      dfd.reject()
    }
  })
}

/**
 * Checks to see if user is already logged into facebook
 * @return {jQuery.promise}
 */
export function checkFacebookStatus() {
  var dfd = $.Deferred()
  _checkFacebookStatus(1, dfd)
  return dfd.promise()
}


/**
 * Sends FB creds to our server, so that the user is logged into our systems
 * @return {jQuery.promise}
 */
function sendFbCreds(response){
  const authResponse = response.authResponse
  const data = {
    'uid': authResponse.userID,
    'accessToken': authResponse.accessToken,
    'agreement': true,
  }
  return sendRequest('facebookLogin', data)
}

/**
 * Pops a facebook login dialog for users to login. It also queries the facebook graph api
 * to retrieve their profile data. This function works for both users who have approved our app
 * and new users.
 *
 * @returns { jQuery promise}
 */
function connectFacebook() {
  var dfd = $.Deferred()
  window.FB.login(_.bind(function(response) {
    if (response.authResponse) {
        window.FB.api('/me', _.bind(function() {
          dfd.resolve(response); // send ORIGINAL response from fb login. The call to fb '/me' is just making sure that we have valid creds
        }, this))
    } else {
      dfd.reject()
    }
  }, this), {scope: 'public_profile,email'})
  return dfd.promise()
}

/**
 * Logs in a facebook user. There are quite a few
 * api calls that are required -- checking facebook auth, fetching fb data from facebook graph, sending
 * fb creds to our servers -- so we utilize promises to make things a bit more straightforward.
 * @return {jQuery promise}
 */
export function loginFacebook() {
  var dfd = $.Deferred()

  // first check to see if they're already logged into facebook
  checkFacebookStatus()
    .done(function(response){
      // user is logged in, so just send fb info to server
      sendFbCreds(response)
      .done(function(resp){
        dfd.resolve(resp)
      })
      .fail(function(){
        dfd.reject("Failed to use facebook credentials to login to our website")
      })
    })
    .fail(function(error) {
      // not logged in yet, so connect to facebook. Note this handles registration for new
      if (error){
        dfd.reject(error)
      } else {
        // users as well
        connectFacebook()
          .done(function(response){
            //
            sendFbCreds(response)
              .done(function(resp){
                dfd.resolve(resp)
              })
              .fail(function(){
                dfd.reject("Failed to use facebook credentials to login to our website")
              })
          })
          .fail(function(){
            dfd.reject("Failed to retrieve your facebook credentials")
          })
       }
      })

  return dfd.promise()
}

export function googleLoginAvailable() {
  return window._google_oauth_client_id && window.google && window.google.accounts
}

export class GoogleLoginButton extends React.Component {
  state = {loading: false}

  componentDidMount() {
    const redirectUri = new URL(window.location)
    redirectUri.pathname = window._google_oauth_redirect_path
    window.google.accounts.id.initialize({
      client_id: window._google_oauth_client_id,
      callback: this.googleCallback,
      login_uri: redirectUri.toString(),
    })
    window.google.accounts.id.renderButton(this.btnContainer, {
      type: "standard",
      theme: "filled_blue",
      size: "large",
      shape: "pill",
      text: this.props.signup ? "signup_with" : "signin_with",
      width: this.btnContainer.offsetWidth,
    })
  }

  // wraps setting this state to loading, running the handler with given data,
  // and then un-setting the loading state when done
  runHandler(data) {
    Promise.all([this.props.handler(data)]).finally(
      () => this.setState({loading: false})
    )
  }

  googleCallback = (credResp) => {
    if (!credResp.clientId || !credResp.credential) {
      this.runHandler({ error: "Google login failed" })
    }

    this.setState({loading: true})
    sendRequest('googleLogin', { oauthCredential: credResp.credential, })
      .done((resp) => this.runHandler(resp))
      .fail((errorMsg) => this.runHandler({ error: errorMsg }))
  }

  render() {
    return <div className="google-login-btn">
      <div ref={(el) => { this.btnContainer = el } } style={this.state.loading ? {display: "none"} : {}} />
      {this.state.loading &&
        <div className="loading_icon">
          <svg id="button-load" x="0px" y="0px" viewBox="0 0 150 150" height='12px' strokeWidth='12px' stroke='#3F3F3E'>
            <circle id="loading_inner" cx="75" cy="75" r="60"/>
          </svg>
        </div>
      }
    </div>
  }
}

export function appleSigninAvailable() {
  return window.AppleID !== undefined
}

// map the error code on ev.detail.error to a human error message
const APPLE_ERRORS = {
  "popup_closed_by_user": "The Sign in with Apple popup was closed",
}

export class AppleSigninButton extends React.Component {
  constructor(props) {
    super(props)
    this.state = {loading: false}
  }

  componentDidMount() {
    window.AppleID.auth.renderButton()

    this.successHandlerBound = this.signInSuccessHandler.bind(this)
    this.errorHandlerBound = this.signInErrorHandler.bind(this)
    document.addEventListener("AppleIDSignInOnSuccess", this.successHandlerBound)
    document.addEventListener("AppleIDSignInOnFailure", this.errorHandlerBound)
  }

  componentWillUnmount() {
    document.removeEventListener("AppleIDSignInOnSuccess", this.successHandlerBound)
    document.removeEventListener("AppleIDSignInOnFailure", this.errorHandlerBound)
  }

  runHandler(data) {
    Promise.all([this.props.handler(data)]).finally(
      () => this.setState({loading: false})
    )
  }

  signInSuccessHandler(ev) {
    this.setState({loading: true})
    // ev.detail should match https://developer.apple.com/documentation/sign_in_with_apple/signinresponsei
    // It should always contain `authorization` (the JWT token details), and for
    // initial signups should also contain `user` (with user name details)
    sendRequest("appleLogin", ev.detail)
      .done((resp) => this.runHandler(resp))
      .fail((errorMsg) => this.runHandler({ error: errorMsg }))
  }

  signInErrorHandler(ev) {
    this.setState({loading: true})
    if (APPLE_ERRORS[ev.detail && ev.detail.error]) {
      this.runHandler({ error: APPLE_ERRORS[ev.detail.error] })
    } else { // fallback for unhandled error types
      Sentry.withScope(function(scope) {
          scope.setExtras({ detail: ev.detail })
          Sentry.captureMessage(`Apple Sign In error: ${ev.detail && ev.detail.error}`, "error")
      })
      this.runHandler({ error: "An error occurred" })
    }
  }

  render() {
    return <div className="apple-login-btn">
      <a id="appleid-signin"
        data-color="black"
        data-border="true"
        data-type={this.props.signup ? "sign-up" : "sign-in"}
        data-height="40px"
        style={this.state.loading ? {display: "none"} : {}}
        ></a>
      {this.state.loading &&
        <div className="loading_icon">
          <svg id="button-load" x="0px" y="0px" viewBox="0 0 150 150" height='12px' strokeWidth='12px' stroke='#3F3F3E'>
            <circle id="loading_inner" cx="75" cy="75" r="60"/>
          </svg>
        </div>
      }
    </div>
  }
}
